// Destino del correo de "recuperar contraseña" (2026-09-13). Medplum arma el
// enlace como `${appBaseUrl}setpassword/<id>/<secret>` (verificado en el
// código del servidor que corre en el VPS) y `appBaseUrl` apunta a hce-chart
// (`https://hce.logiacare.com/hce/paciente/`). Antes esta página la servía el
// legado packages/hce (`SetPasswordForm` de @medplum/react). Sin sesión de
// Medplum (ruta en PREFIJOS_SIN_MEDPLUM de main.tsx): `auth/setpassword` pasa
// por el proxy de la bóveda, que solo bloquea `admin/*`.
import { normalizeErrorString } from '@medplum/core';
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ListaReglasContrasena } from './ListaReglasContrasena';
import { contrasenaCumplePolitica } from './reglasContrasena';

export function NuevaContrasenaPage(): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { id = '', secret = '' } = useParams();
  const [password, setPassword] = useState('');
  const [confirmacion, setConfirmacion] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exito, setExito] = useState(false);

  async function enviar(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (password !== confirmacion) {
      setError(t('auth.nuevaContrasena.noCoinciden'));
      return;
    }
    setEnviando(true);
    try {
      await medplum.post('auth/setpassword', { id, secret, password });
      setExito(true);
    } catch (err) {
      setError(`${t('auth.nuevaContrasena.error')} (${normalizeErrorString(err)})`);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="min-h-screen bg-hce-paper flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm bg-hce-surface border border-border rounded-lg shadow-sm p-6 space-y-4">
        <h1 className="text-base font-medium text-hce-ink">{t('auth.nuevaContrasena.titulo')}</h1>
        {exito ? (
          <>
            <Alert>
              <AlertDescription>{t('auth.nuevaContrasena.exito')}</AlertDescription>
            </Alert>
            <Button asChild className="w-full">
              <Link to="/login">{t('auth.nuevaContrasena.irALogin')}</Link>
            </Button>
          </>
        ) : (
          <form onSubmit={(e) => void enviar(e)} className="space-y-3">
            {error && (
              <Alert variant="red">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div>
              <label htmlFor="nueva-contrasena" className="block text-xs font-mono uppercase tracking-wide text-hce-muted mb-1">
                {t('auth.nuevaContrasena.contrasena')}
              </label>
              <Input id="nueva-contrasena" type="password" minLength={12} value={password} onChange={(e) => setPassword(e.target.value)} required />
              <div className="mt-1.5">
                <ListaReglasContrasena password={password} />
              </div>
            </div>
            <div>
              <label htmlFor="nueva-contrasena-confirmacion" className="block text-xs font-mono uppercase tracking-wide text-hce-muted mb-1">
                {t('auth.nuevaContrasena.confirmacion')}
              </label>
              <Input id="nueva-contrasena-confirmacion" type="password" minLength={12} value={confirmacion} onChange={(e) => setConfirmacion(e.target.value)} required />
            </div>
            <Button type="submit" disabled={enviando || !contrasenaCumplePolitica(password)} className="w-full">
              {enviando ? t('auth.nuevaContrasena.guardando') : t('auth.nuevaContrasena.guardar')}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
